import React from 'react';
import { HTTP_STATUS, API_CONFIG } from '../config/api';

/**
 * Centralized error handling utilities
 * Provides consistent error processing, retry logic, and user-friendly messages
 */
class ErrorHandler {
  /**
   * Handle API errors and return user-friendly error information
   */
  static handleApiError(error, context = '') {
    console.error(`API Error in ${context}:`, error);

    // Handle authentication errors
    if (error.status === HTTP_STATUS.UNAUTHORIZED) {
      return {
        message: 'Your session has expired. Please log in again.',
        type: 'auth',
        retry: false,
        shouldRedirect: true
      };
    }

    // Handle forbidden access
    if (error.status === HTTP_STATUS.FORBIDDEN) {
      return {
        message: 'You do not have permission to perform this action.',
        type: 'permission',
        retry: false,
        shouldRedirect: false
      };
    }

    // Handle not found errors
    if (error.status === HTTP_STATUS.NOT_FOUND) {
      return {
        message: 'The requested resource was not found.',
        type: 'notFound',
        retry: false,
        shouldRedirect: false
      };
    }

    // Handle server errors
    if (error.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      return {
        message: 'Server error. Please try again later.',
        type: 'server',
        retry: true,
        shouldRedirect: false
      };
    }

    // Handle client errors
    if (error.status >= HTTP_STATUS.BAD_REQUEST && error.status < HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      return {
        message: error.message || 'Invalid request. Please check your input.',
        type: 'client',
        retry: false,
        shouldRedirect: false
      };
    }

    // Handle network errors
    if (error.name === 'NetworkError' || error.name === 'TypeError') {
      return {
        message: 'Network error. Please check your internet connection.',
        type: 'network',
        retry: true,
        shouldRedirect: false
      };
    }

    // Handle timeout errors
    if (error.name === 'TimeoutError' || error.name === 'AbortError') {
      return {
        message: 'Request timeout. Please try again.',
        type: 'timeout',
        retry: true,
        shouldRedirect: false
      };
    }

    // Handle validation errors
    if (error.data && error.data.errors) {
      return {
        message: this.formatValidationErrors(error.data.errors),
        type: 'validation',
        retry: false,
        shouldRedirect: false
      };
    }

    // Default error handling
    return {
      message: error.message || 'An unexpected error occurred. Please try again.',
      type: 'unknown',
      retry: true,
      shouldRedirect: false
    };
  }

  /**
   * Format validation errors into user-friendly messages
   */
  static formatValidationErrors(errors) {
    if (Array.isArray(errors)) {
      return errors.join(', ');
    }
    
    if (typeof errors === 'object') {
      return Object.values(errors).flat().join(', ');
    }
    
    return errors.toString();
  }

  /**
   * Retry logic with exponential backoff
   */
  static async retryOperation(operation, maxRetries = API_CONFIG.RETRY_ATTEMPTS, baseDelay = API_CONFIG.RETRY_DELAY) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        // Don't retry on certain error types
        const errorInfo = this.handleApiError(error);
        if (!errorInfo.retry || attempt === maxRetries) {
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = baseDelay * Math.pow(2, attempt);
        await this.delay(delay);
      }
    }
    
    throw lastError;
  }

  /**
   * Create a delay promise
   */
  static delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Handle multiple simultaneous errors and prioritize them
   */
  static prioritizeErrors(errors) {
    if (!Array.isArray(errors) || errors.length === 0) {
      return null;
    }

    // Priority order: auth > server > network > client > unknown
    const priorityOrder = ['auth', 'server', 'network', 'client', 'validation', 'unknown'];
    
    const errorsByType = {};
    errors.forEach(error => {
      const errorInfo = this.handleApiError(error);
      if (!errorsByType[errorInfo.type]) {
        errorsByType[errorInfo.type] = [];
      }
      errorsByType[errorInfo.type].push(errorInfo);
    });

    // Find the highest priority error type
    for (const type of priorityOrder) {
      if (errorsByType[type]) {
        const primaryError = errorsByType[type][0];
        const count = errorsByType[type].length;
        
        return {
          ...primaryError,
          message: count > 1 
            ? `${primaryError.message} (${count} similar errors)`
            : primaryError.message,
          totalErrors: errors.length
        };
      }
    }

    return this.handleApiError(errors[0]);
  }

  /**
   * Create error boundary for React components
   */
  static createErrorBoundary(fallbackComponent) {
    return class ErrorBoundary extends React.Component {
      constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
      }

      static getDerivedStateFromError(error) {
        return { hasError: true, error };
      }

      componentDidCatch(error, errorInfo) {
        console.error('Error Boundary caught an error:', error, errorInfo);
      }

      render() {
        if (this.state.hasError) {
          return fallbackComponent ? 
            fallbackComponent(this.state.error) : 
            React.createElement('div', { 
              style: { padding: '20px', textAlign: 'center' } 
            }, 'Something went wrong. Please refresh the page.');
        }

        return this.props.children;
      }
    };
  }

  /**
   * Log errors for debugging and monitoring
   */
  static logError(error, context = '', additionalInfo = {}) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      context,
      message: error.message,
      stack: error.stack,
      status: error.status,
      name: error.name,
      ...additionalInfo
    };

    console.error('Error Log:', errorLog);
    
    // In production, you might want to send this to a logging service
    // Example: sendToLoggingService(errorLog);
  }
}

export default ErrorHandler;